import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const MOBILE_QUERY = '(max-width: 768px)';

// Panels that "hold" the track: while one is centred, the horizontal
// translation pauses for a stretch of scrolling and that panel consumes the
// scroll instead (to advance its own internal slides), then the track
// resumes. A panel opts in with data-hold="<id>" and the component inside it
// registers a matching handler here.
//
// This is deliberately NOT a nested ScrollTrigger pin. A pin inside the
// already-pinned, x-translated track measures against a moving ancestor and
// is exactly the fragile pattern that was removed from HomeProjects — and
// that leaked a trigger from this very gallery. Folding the dwell into the
// master timeline keeps a single source of truth for scroll position.
const holdRegistry = new Map();

export function useScrollHold(id, { length, onProgress }) {
  onMounted(() => {
    holdRegistry.set(id, { length, onProgress });
    // The pin is built on the page's mount, which may already have happened,
    // so ask for a rebuild now that this panel's dwell length is known.
    requestAnimationFrame(() => {
      const state = getSharedState();
      state.value.rebuild?.();
    });
  });

  onBeforeUnmount(() => {
    holdRegistry.delete(id);
  });
}

// Shared across the app so nav (rendered outside <main>) can reach
// whichever page currently owns the horizontal track.
function getSharedState() {
  return useState('horizontalScroll', () => ({
    isHorizontal: false,
    restoring: false,
    scrollToPanel: null,
    rebuild: null,
    // id of the panel currently on screen, so the nav can mark itself.
    activeSection: null,
  }));
}

const POSITION_KEY = 'h-scroll-position';
// The track's own offset, saved alongside the scroll position. The blocking
// script in nuxt.config reads this one: it can place the track before the first
// paint, which the scroll position alone cannot do, since the pin that makes
// the page scrollable does not exist yet at that point.
const TRACK_X_KEY = 'h-scroll-x';
// The gallery rail inside the dwell, saved for the same reason and read by the
// same script. Placing the track alone still opened on the first picture and
// then jumped to the one that was actually on screen.
const RAIL_X_KEY = 'h-scroll-rail-x';
// Which frame was centred. Placing the rail alone still left the focus on the
// opening picture, because that is the one the server rendered it on and only
// hydration knows better.
const RAIL_INDEX_KEY = 'h-scroll-rail-i';

// The one the blocking script in nuxt.config restores for every other long page
// on the site. Nothing on a phone can place this page before it is painted: the
// pin is never built there, so there is no track to translate and no pinned
// height to scroll against, and the offset above is only ever read on a desktop.
// That left the browser to restore it natively, which it does once the document
// has grown, which is the jump to the hero and back.
//
// Same shape as usePageScroll writes, because it is the same script reading it.
const PAGE_KEY = 'page-scroll';
const pageKeyFor = (path) => `${PAGE_KEY}:${path.replace(/\/+$/, '') || '/'}`;

export function useHorizontalScroll() {
  const state = getSharedState();
  const lenis = useLenis();
  const route = useRoute();

  let wrapperEl = null;
  let trackEl = null;
  let scrollTriggerInstance = null;
  let mediaQueryList = null;
  let resizeRaf = null;
  let timeline = null;
  let holds = [];
  // Captured when this page starts, not read at teardown: by the time the
  // outgoing page unmounts the router has already moved on, so `route.path`
  // would name the page being navigated TO and save under the wrong key.
  let ownPath = null;
  let scrollRaf = null;

  // Which panel is on screen right now. Driven from scroll rather than
  // IntersectionObserver because the panels are moved by a transform on the
  // track — their layout positions never change, so IO thresholds wouldn't
  // fire as the track slides past.
  function updateActiveSection() {
    if (!trackEl) return;

    const list = panels();
    if (!list.length) return;

    const horizontal = state.value.isHorizontal;
    const midX = window.innerWidth / 2;
    const midY = window.innerHeight / 2;

    const current = list.find((panel) => {
      const r = panel.getBoundingClientRect();
      return horizontal
        ? r.left <= midX && r.right >= midX
        : r.top <= midY && r.bottom >= midY;
    });

    const id = current?.querySelector('[id]')?.id || null;
    if (id && state.value.activeSection !== id) {
      state.value.activeSection = id;
    }
  }

  // Remembering where the reader was means returning from /projects lands
  // back on the Projects panel rather than at the top of the hero.
  // sessionStorage rather than in-memory state so it also survives a reload.
  // Set while a restore is being applied. On a client-side return the outgoing
  // page is briefly still in the DOM, so the document is taller than it is
  // about to be and the restore clamps against that. The scroll events it
  // fires would then persist the clamped value, and the next return would
  // start from it: the position walked further off on every trip.
  let restoring = false;

  // Mirrored onto the shared state because a panel's dwell has to know too.
  // The gallery is placed by its own handler, and a report arriving before the
  // page has been put back would move it off the restored position.
  function setRestoring(value) {
    restoring = value;
    state.value.restoring = value;
  }

  // Set across a pin rebuild. Killing the pin reverts its spacer, which
  // collapses the document, which makes the browser clamp the scroll position
  // and fire a scroll event for it. That event is the page rearranging itself,
  // not the reader moving, and saving what it reports overwrote the position
  // being restored with something near zero.
  //
  // It is not a rare window. The gallery asks for a rebuild at the end of its
  // own mount, once the live pictures have been fetched and the first few
  // decoded, which is comfortably past the moment the restore stops guarding.
  let rebuilding = false;

  function savePosition() {
    if (typeof window === 'undefined' || !ownPath || restoring || rebuilding) return;
    try {
      sessionStorage.setItem(`${POSITION_KEY}:${ownPath}`, String(Math.round(window.scrollY)));

      // Only while stacked. On a desktop the pin and the preset above already
      // place this page, and a second mechanism scrolling as well would be two
      // things arriving at the same answer by different routes.
      if (mediaQueryList?.matches) {
        sessionStorage.setItem(pageKeyFor(ownPath), String(Math.round(window.scrollY)));
        sessionStorage.setItem(
          `page-height:${ownPath.replace(/\/+$/, '') || '/'}`,
          String(Math.round(document.documentElement.scrollHeight))
        );
      }

      // Read off the element rather than computed from progress, so it is
      // exactly what was on screen. Only while the track is actually
      // translated: on a phone there is no transform to restore, and writing
      // one would offset a layout that never asked for it.
      if (state.value.isHorizontal && trackEl) {
        const x = new DOMMatrixReadOnly(getComputedStyle(trackEl).transform || '').m41;
        sessionStorage.setItem(`${TRACK_X_KEY}:${ownPath}`, String(Math.round(x)));

        const railEl = document.querySelector('.h-scroll-track .rail');
        if (railEl) {
          const rx = new DOMMatrixReadOnly(getComputedStyle(railEl).transform || '').m41;
          sessionStorage.setItem(`${RAIL_X_KEY}:${ownPath}`, String(Math.round(rx)));

          const frames = railEl.querySelectorAll('.frame');
          const active = railEl.querySelector('.frame.is-active');
          const index = Array.prototype.indexOf.call(frames, active);
          if (index >= 0) {
            sessionStorage.setItem(`${RAIL_INDEX_KEY}:${ownPath}`, String(index));
          }
        } else {
          sessionStorage.removeItem(`${RAIL_X_KEY}:${ownPath}`);
          sessionStorage.removeItem(`${RAIL_INDEX_KEY}:${ownPath}`);
        }
      }
      // No else. There is no pin below the breakpoint and none for the moment
      // either side of a rebuild, and neither is a statement about where the
      // track belongs: the offsets are simply not knowable just then. Clearing
      // them here meant a phone wiped the desktop position on its first scroll,
      // and a rebuild wiped it on the very load that had just used it.
      //
      // Nothing is stranded by keeping them. The blocking script that reads
      // them does not run below 768px and refuses any offset that is not
      // negative, and the stacked stylesheet overrides the track's transform
      // outright.
    } catch {
      // private mode / storage disabled — losing the position is harmless
    }
  }

  // Kept current on every scroll, not just on unmount. Saving only at teardown
  // left a stale value behind: scrolling back to the hero and reloading would
  // restore wherever you were when you last navigated away, because a reload
  // never runs the component's unmount hook.
  let settleSaveTimer = null;

  function handleScroll() {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = null;
      savePosition();
    });

    // And once more after scrolling stops. The scroll position is final the
    // instant the wheel stops, but the track is still easing toward it, so the
    // offset saved during the scroll is behind the position saved with it. A
    // reload would then place the track short of the panel and visibly close
    // the gap. This trailing save records the two in agreement.
    clearTimeout(settleSaveTimer);
    settleSaveTimer = setTimeout(savePosition, 1300);
  }

  // Throttled to ~8Hz: the scrub tween eases the track into place over about
  // a second after scrolling stops, so this has to keep sampling past the
  // last scroll event — but it reads layout, so it shouldn't run every frame.
  let lastActiveCheck = 0;
  function tickActiveSection(time) {
    if (time - lastActiveCheck < 0.12) return;
    lastActiveCheck = time;
    updateActiveSection();
  }

  function restorePosition() {
    if (typeof window === 'undefined' || !ownPath) return false;
    let saved = null;
    try {
      saved = sessionStorage.getItem(`${POSITION_KEY}:${ownPath}`);
    } catch {
      return false;
    }

    const y = Number.parseInt(saved ?? '', 10);
    if (!Number.isFinite(y) || y <= 0) return false;

    // The pin's own end, not the document height. On a client-side return the
    // document is still the size of the page being left, so clamping against
    // it cut the target down to wherever the reader had been on that page.
    const max = scrollTriggerInstance
      ? scrollTriggerInstance.end
      : document.documentElement.scrollHeight - window.innerHeight;
    const target = Math.min(y, Math.max(max, 0));

    // Jump rather than animate: this is restoring context, not navigating,
    // so a long smooth scroll from the top would be the wrong feel.
    setRestoring(true);
    // Lenis first, and forced. It keeps its own scroll position and writes it
    // to the window every frame, so arriving from another page it still held
    // that page's offset and put it straight back over the scroll below.
    lenis?.resize?.();
    lenis?.scrollTo?.(target, { immediate: true, force: true, lock: true });
    window.scrollTo(0, target);
    ScrollTrigger.update();

    // The scroll position lands instantly, but the track does not follow it
    // instantly: the pin is scrubbed, so it eases toward the new position over
    // about a second. That easing is what was visible as the hero appearing
    // first and sliding away to wherever you had been.
    //
    // Finishing the scrub tween outright puts the track where the scroll
    // position already says it should be, before anything is painted.
    settlePin();

    // Two frames, so the scroll events this just fired have flushed before
    // saving is allowed to record anything again.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setRestoring(false);
        // Now that panels will listen again, re-report. The saved offset a
        // panel opened itself on was recorded mid-scrub and can be a few
        // pixels short of the settled one, and with the page sitting still
        // there is no scroll coming to correct it.
        ScrollTrigger.update();
      });
    });
    return true;
  }

  // The blocking script in nuxt.config writes a stylesheet rule that places the
  // track before the first paint. GSAP's inline transform outranks it the
  // moment the pin runs, so this is tidying rather than a handover: left in
  // place it would strand the track at a stale offset if the pin were ever
  // torn down.
  function clearPreset() {
    if (typeof document === 'undefined') return;
    document.getElementById('hscroll-preset')?.remove();
  }

  // Collapses the pin's in-flight scrub so the track matches the current
  // scroll position with no travel. Safe to call when there is no pin.
  function settlePin() {
    if (!scrollTriggerInstance || !timeline) return;
    const scrub = scrollTriggerInstance.getTween?.();
    if (scrub) scrub.progress(1);
    // Set directly as well: getTween returns nothing when no scrub is running,
    // which is exactly the case on a fresh load.
    timeline.progress(scrollTriggerInstance.progress);
  }

  // Panels declaring data-hold, in track order, with the dwell length each
  // registered component asked for. Only panels whose component has actually
  // registered count — otherwise we'd reserve scroll distance for a dwell
  // that nothing drives.
  function panels() {
    return trackEl ? Array.from(trackEl.querySelectorAll(':scope > .h-scroll-panel')) : [];
  }

  function getHolds() {
    if (!trackEl) return [];
    return panels()
      .filter((el) => el.dataset && el.dataset.hold && holdRegistry.has(el.dataset.hold))
      .map((el) => {
        const entry = holdRegistry.get(el.dataset.hold);
        return {
          id: el.dataset.hold,
          x: el.offsetLeft,
          length: Math.max(entry.length(), 0),
          onProgress: entry.onProgress,
        };
      })
      .filter((h) => h.length > 0)
      .sort((a, b) => a.x - b.x);
  }

  function createPin() {
    if (!wrapperEl || !trackEl || scrollTriggerInstance) return;

    // Take ownership of the transform before any tween is built. GSAP reads
    // the computed transform to decide where a tween to `x` starts, and the
    // blocking script in nuxt.config has left a stylesheet rule translating
    // the track to wherever the last reload ended. Without this the timeline
    // is built from that offset rather than from zero, so the track renders
    // further along than the scroll position asks for, savePosition writes
    // that further-along offset back, and the next reload builds on it again:
    // every reload walks the page forward, converging on the end of the track.
    gsap.set(trackEl, { x: 0 });

    const distance = Math.max(trackEl.scrollWidth - wrapperEl.offsetWidth, 0);
    holds = getHolds();
    const totalHold = holds.reduce((sum, h) => sum + h.length, 0);

    // Durations are expressed in pixels of scroll, and `end` is the same
    // total, so timeline progress maps 1:1 onto scroll distance. That keeps
    // the movement speed identical before and after a dwell.
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: wrapperEl,
        start: 'top top',
        end: `+=${distance + totalHold}`,
        pin: true,
        scrub: 1,
        anticipatePin: 1,
      },
    });

    let cursor = 0;
    holds.forEach((hold) => {
      const travel = hold.x - cursor;
      if (travel > 0) {
        tl.to(trackEl, { x: -hold.x, ease: 'none', duration: travel });
      }

      // x is untouched here: the track sits still while this proxy sweeps
      // 0 -> 1, handing the panel its own scroll progress.
      const proxy = { p: 0 };
      tl.to(proxy, {
        p: 1,
        ease: 'none',
        duration: hold.length,
        onUpdate: () => hold.onProgress(proxy.p),
      });

      cursor = hold.x;
    });

    if (distance > cursor) {
      tl.to(trackEl, { x: -distance, ease: 'none', duration: distance - cursor });
    }

    scrollTriggerInstance = tl.scrollTrigger;
    timeline = tl;
    state.value.isHorizontal = true;
  }

  // Durations are baked in pixels at build time, so a resize (or a late hold
  // registration) needs a genuine rebuild rather than a refresh.
  // A rebuild must leave the reader where it found them.
  //
  // Killing the pin reverts the spacer GSAP injected, and the document loses
  // every pixel of height the pin was giving it: on this page what is left is a
  // single screen. The browser clamps the scroll position to fit, and building
  // the pin again gives the height back but not the position. Nothing
  // downstream can tell the difference between that and the reader being at the
  // top, so the track resets, the rail returns to its first frame, and the next
  // save writes it all down as where they were.
  //
  // ScrollTrigger.refresh() protects its own reverts this way. This one is
  // ours, so it is ours to undo.
  //
  // It is the gallery that made this visible: it asks for a rebuild at the end
  // of its mount, once the live pictures are fetched and the first few decoded,
  // and whether that lands before or after the load-event restore is a race.
  // Landing after it, there was nothing left to put the page back.
  function rebuildPin() {
    if (!wrapperEl || !trackEl) return;
    if (mediaQueryList && mediaQueryList.matches) return;

    const y = window.scrollY;

    rebuilding = true;
    destroyPin();
    createPin();
    ScrollTrigger.refresh();

    if (y > 0 && Math.abs(window.scrollY - y) > 1) {
      // The same order restorePosition uses, and for the same reason: Lenis
      // holds a position of its own and writes it to the window every frame,
      // so it has to be told before the window is.
      lenis?.resize?.();
      lenis?.scrollTo?.(y, { immediate: true, force: true, lock: true });
      window.scrollTo(0, y);
      ScrollTrigger.update();
      // The scrub would ease the track across from wherever the collapse left
      // it, which is the whole width of the gallery. Finish it outright.
      settlePin();
    }

    // A frame later, not on the next line: the scroll the collapse causes is
    // delivered as an event after this task, and handleScroll defers a frame
    // again on top of that.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        rebuilding = false;
      });
    });
  }

  function destroyPin() {
    if (scrollTriggerInstance) {
      // kill(true) — revert. Without the revert flag GSAP leaves the
      // pin-spacer it injected in the DOM, so navigating between the two
      // horizontally-scrolled pages accumulated stale spacers and left the
      // document height wrong for the incoming page.
      scrollTriggerInstance.kill(true);
      scrollTriggerInstance = null;
    }
    if (timeline) {
      timeline.kill();
      timeline = null;
    }
    holds = [];
    if (trackEl) {
      gsap.set(trackEl, { clearProps: 'transform' });
    }
    state.value.isHorizontal = false;
  }

  function handleBreakpointChange(e) {
    if (e.matches) {
      destroyPin();
      requestAnimationFrame(() => ScrollTrigger.refresh());
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          createPin();
          ScrollTrigger.refresh();
        });
      });
    }
  }

  function handleResize() {
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(rebuildPin);
  }

  function scrollToPanel(target) {
    const el = typeof target === 'string' ? document.querySelector(target) : target;
    if (!el) return;

    if (!state.value.isHorizontal || !wrapperEl || !trackEl || !scrollTriggerInstance) {
      // Vertical/mobile mode: normal in-flow scroll to the element.
      const y = el.getBoundingClientRect().top + window.scrollY;
      lenis?.scrollTo(y, { duration: 1.2 });
      return;
    }

    // Measure the panel, never the section inside it. offsetLeft is relative
    // to the offsetParent, and panels are positioned, so a section's own
    // offsetLeft is 0 — which sent every nav link back to the start of the
    // track. Panels are direct children of the track, so their offsetLeft is
    // the track-space coordinate getHolds() also records.
    const panel = el.closest('.h-scroll-panel') || el;
    const targetX = panel.offsetLeft;

    // Scroll offset is no longer proportional to x once holds exist: every
    // dwell before the target adds its full length to the distance you must
    // scroll to reach it. Walk the holds and add them back.
    const holdBefore = holds
      .filter((h) => h.x < targetX)
      .reduce((sum, h) => sum + h.length, 0);

    const st = scrollTriggerInstance;
    const targetY = Math.min(st.start + targetX + holdBefore, st.end);
    lenis?.scrollTo(targetY, { duration: 1.2 });
  }

  // Scrolls to a given point *inside* a dwell rather than to its start.
  //
  // The master timeline is built in scroll pixels — every duration is a pixel
  // count and `end` is their sum — so a hold's progress maps straight onto a
  // scroll offset: the panel's own x, plus every dwell before it, plus however
  // far into this one you want to be.
  // `immediate` is for arriving rather than moving. A link somebody was sent
  // names a picture, and gliding there over a second and a bit shows them the
  // sixteen pictures in between instead of the one they opened it for.
  function scrollToHold(id, progress, { immediate = false } = {}) {
    const st = scrollTriggerInstance;
    if (!st || !state.value.isHorizontal) return false;

    const hold = holds.find((h) => h.id === id);
    if (!hold || !hold.length) return false;

    const before = holds
      .filter((h) => h.x < hold.x)
      .reduce((sum, h) => sum + h.length, 0);

    const p = Math.min(Math.max(progress, 0), 1);
    const y = Math.min(st.start + hold.x + before + p * hold.length, st.end);

    if (immediate) {
      // The same order restorePosition uses, and for the same reason: Lenis
      // holds its own position and writes it to the window every frame, so it
      // has to be told first or it puts the old one straight back. settlePin
      // then collapses the scrub, which is what would otherwise ease the track
      // across after the scroll had already landed.
      lenis?.scrollTo(y, { immediate: true, force: true, lock: true });
      window.scrollTo(0, y);
      ScrollTrigger.update();
      settlePin();
      return true;
    }

    lenis?.scrollTo(y, { duration: 1.2 });
    return true;
  }

  function start(wrapper, track) {
    wrapperEl = unref(wrapper);
    trackEl = unref(track);
    if (!wrapperEl || !trackEl || typeof window === 'undefined') return;

    ownPath = route.path;

    // Held shut until this page has been put where it belongs. Arriving from
    // another page, the window is still sitting at that page's offset, and the
    // scroll events it emits during the transition would be recorded as this
    // page's position, overwriting the one we are about to restore.
    setRestoring(true);

    mediaQueryList = window.matchMedia(MOBILE_QUERY);
    mediaQueryList.addEventListener('change', handleBreakpointChange);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    gsap.ticker.add(tickActiveSection);

    // An explicit hash is a deliberate destination, so it outranks both the
    // saved position and the offset the track was preset to.
    // A hash is a destination somebody asked for. Coming back is not: the
    // entry being returned to can still carry the hash it was first opened
    // with, and honouring it would scroll to that section rather than to
    // wherever the reader actually left off.
    let cameBack = false;
    try {
      cameBack = sessionStorage.getItem('nav-back') === '1';
      if (cameBack) sessionStorage.removeItem('nav-back');
    } catch {
      // private mode / storage disabled
    }

    const hash = cameBack ? '' : window.location.hash;

    // The restored entry can put its old hash back in the address bar even
    // though nothing acted on it.
    if (cameBack && window.location.hash) clearUrlHash();
    // Whether this load arrived with the track already placed. Has to be read
    // before createPin, which takes the transform over inline.
    if (!mediaQueryList.matches) {
      createPin();

      // Only the pin gives the document the height to scroll to, so this
      // cannot happen any earlier — and must not happen any later. createPin
      // through to here is one task, so no frame is painted in between.
      // Deferring it by even a frame paints the track at zero, which is the
      // hero, which is the snap this exists to avoid.
      //
      // It used to require the preset element, which only a full page load
      // creates. That is why a reload was smooth and a return from a nested
      // page was not: the client-side case skipped this and waited for the
      // deferred restore below, one paint too late.
      if (!hash) {
        ScrollTrigger.refresh();
        restorePosition();
      }
    }

    // Safe in every case now: on a phone there was never a pin to wait for,
    // and on desktop GSAP's inline transform already outranks the rule.
    clearPreset();

    state.value.scrollToPanel = scrollToPanel;
    state.value.scrollToHold = scrollToHold;
    state.value.rebuild = rebuildPin;

    // Every path has to release it. A phone builds no pin, a hash link skips
    // the restore, and a first visit has nothing saved: none of those reach
    // restorePosition, and the page would then never record its position.
    setTimeout(() => {
      setRestoring(false);
    }, 1200);

    // On a client-side route change the incoming page mounts while the
    // outgoing one is still in the DOM, so the pin above is measured against
    // a document that is about to shrink. Re-measure once the transition has
    // settled, and let Lenis pick up the corrected height — otherwise the
    // page can be left believing it has nothing to scroll.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        ScrollTrigger.refresh();
        lenis?.resize?.();
        // Only after the refresh, since the document has its final height by
        // then — restoring before it would clamp against a stale scrollHeight.
        // An explicit hash is a deliberate destination, so it outranks
        // wherever the reader happened to be last time.
        if (!hash) restorePosition();
        updateActiveSection();

        // Once more when the page has finished loading. Images and fonts land
        // after this frame and change the document's height, so the first
        // restore can clamp against a measurement that is about to be wrong,
        // leaving the track short of where it belongs and correcting itself
        // visibly a moment later.
        const settle = () => {
          ScrollTrigger.refresh();
          lenis?.resize?.();
          if (!hash) restorePosition();
          updateActiveSection();
        };

        if (document.readyState === 'complete') {
          requestAnimationFrame(settle);
        } else {
          window.addEventListener('load', () => requestAnimationFrame(settle), { once: true });
          // A load event that never comes must not leave the page hidden. The
          // blocking script has its own cap; this is the one that still puts
          // the track in the right place.
          setTimeout(settle, 1200);
        }
      });
    });

    // Landing on this page via a hash link (e.g. a cross-page
    // "/explore#blog") skips our nav hijack entirely (full navigation) —
    // correct the position once layout/pin has settled.
    if (hash) {
      const targetEl = document.getElementById(hash.slice(1));
      if (targetEl) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollToPanel(targetEl);
            // Only now, once it has been acted on: the hash has served its
            // purpose and stops earning its place in the address bar.
            clearUrlHash();
          });
        });
      } else {
        clearUrlHash();
      }
    }
  }

  function stop() {
    clearTimeout(settleSaveTimer);
    // Before destroyPin: killing the pin reverts the spacer and collapses the
    // document height, which resets scrollY and would save 0 every time.
    savePosition();
    destroyPin();

    if (mediaQueryList) {
      mediaQueryList.removeEventListener('change', handleBreakpointChange);
      mediaQueryList = null;
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    }
    // The offset is forgotten on the way out: it is meant to survive a reload,
    // and leaving the page is the only thing that runs this. Kept, it would
    // outlive the visit and drop a later one where an earlier one ended.
    //
    // The height beside it is kept, for the reason usePageScroll keeps its own.
    // It is not a position, it stays true, and the next load reserves it before
    // anything else has run.
    try {
      if (ownPath) sessionStorage.removeItem(pageKeyFor(ownPath));
    } catch {
      // private mode / storage disabled
    }

    gsap.ticker.remove(tickActiveSection);
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    if (scrollRaf) cancelAnimationFrame(scrollRaf);
    state.value.activeSection = null;

    if (state.value.scrollToPanel === scrollToPanel) {
      state.value.scrollToPanel = null;
    }
    if (state.value.scrollToHold === scrollToHold) {
      state.value.scrollToHold = null;
    }
    if (state.value.rebuild === rebuildPin) {
      state.value.rebuild = null;
    }

    wrapperEl = null;
    trackEl = null;
  }

  return { start, stop, scrollToPanel, scrollToHold, state };
}
